import { useCallback, useEffect, useMemo, useState } from "react";

import { useHookableRef } from "./useHookableRef";

import type { MutableRefObject, SyntheticEvent } from "react";

export enum ScrollDirection {
  Vertical = "vertical",
  Horizontal = "horizontal",
}

export interface ScrollContainerOptions {
  scrollDirection?: ScrollDirection;
  margin?: number;
  containerRect?: DOMRect; // Allows shadow update on container resize, optional.
}

export interface ScrollShadowValues {
  boxShadow?: string;
  width?: number;
  onScroll: (event: SyntheticEvent) => void;
}

/**
 * Used to apply shadow styles to a scrolling container.
 */
export function useScrollWithShadow<T extends HTMLElement>(
  {
    scrollDirection,
    margin,
    containerRect: contentRect,
  }: ScrollContainerOptions = {
    scrollDirection: ScrollDirection.Vertical,
    margin: 5,
  },
): [MutableRefObject<T | null>, ScrollShadowValues] {
  const [container, setContainer] = useState<T | null>(null);
  const containerRef = useHookableRef<T | null>(null, (v) => {
    setContainer(v);
    return v;
  });

  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollLength, setScrollLength] = useState(0);
  const [clientLength, setClientLength] = useState(0);

  const handleScroll = useCallback(
    (event: SyntheticEvent) => {
      const target = event.currentTarget;

      if (scrollDirection === ScrollDirection.Vertical) {
        setScrollPosition(target?.scrollTop ?? 0);
        setScrollLength(target?.scrollHeight ?? 0);
        setClientLength(target?.clientHeight ?? 0);
      } else {
        setScrollPosition(target?.scrollLeft ?? 0);
        setScrollLength(target?.scrollWidth ?? 0);
        setClientLength(target?.clientWidth ?? 0);
      }
    },
    [scrollDirection],
  );

  const boxShadow = useMemo(() => {
    const isEnd = clientLength + scrollPosition > scrollLength - (margin ?? 0);
    const isStart = scrollPosition <= (margin ?? 0);
    const isBetween = !isEnd && !isStart;

    let boxShadow;

    const start = "var(--shadow-inner-start)";
    const end = "var(--shadow-inner-end)";

    if (isStart && isEnd) return "";
    if (isStart) {
      boxShadow = end;
    } else if (isEnd) {
      boxShadow = start;
    } else if (isBetween) {
      boxShadow = `${start}, ${end}`;
    }

    return boxShadow;
  }, [clientLength, scrollLength, scrollPosition, margin]);

  // Apply the shadows once the container is rendered
  useEffect(() => {
    if (container) {
      // Timeout ensures that the target's scrollHeight and clientHeight are rendered.
      setTimeout(
        () =>
          handleScroll({
            currentTarget: container,
          } as unknown as SyntheticEvent),
        1,
      );
    }
  }, [container, handleScroll]);

  useEffect(() => {
    handleScroll({
      currentTarget: container,
    } as unknown as SyntheticEvent);
  }, [container, contentRect?.height, contentRect?.width, handleScroll]);

  return [
    containerRef,
    {
      boxShadow,
      width: container?.clientWidth,
      onScroll: handleScroll,
    },
  ];
}
